import * as THREE from "three";
import { getItemDef, type ItemKind, type Vec3 } from "@rietz/shared";
import type { WorldItemSnap } from "../net/socket.js";
import {
  ITEM_COLOR_AMMO,
  ITEM_COLOR_ARMOR,
  ITEM_COLOR_CONTAINER,
  ITEM_COLOR_LOOT,
  ITEM_COLOR_MED,
  ITEM_COLOR_WEAPON,
  hashString,
  rand01,
} from "../render/palette.js";

/**
 * Loose loot on the ground as one instanced mesh. The set changes at HUMAN
 * speed, so the whole instance buffer is rewritten on every change — a few
 * dozen matrix writes seconds apart, buying the invariant that the drawn set is
 * a pure function of the tracked set (no ghost boxes after a resync).
 * Per-instance colour is the item's KIND. No DOM.
 */

/** Edge length of a dropped item's box, metres. Small enough to read as an
 *  object on the floor, big enough to spot against grass at ten paces. */
const ITEM_SIZE_M = 0.25;

/**
 * Instances the pool can draw. Every item is TRACKED; only the first
 * `MAX_DRAWN` are drawn, so interaction keeps working on an item the pool ran
 * out of room for. 256 is far past anything eight players leave about.
 */
const MAX_DRAWN = 256;

/** Colour per item kind. `container` here means a rig/backpack lying loose. */
const KIND_COLORS: Record<ItemKind, number> = {
  ammo: ITEM_COLOR_AMMO,
  med: ITEM_COLOR_MED,
  weapon: ITEM_COLOR_WEAPON,
  armor: ITEM_COLOR_ARMOR,
  container: ITEM_COLOR_CONTAINER,
  loot: ITEM_COLOR_LOOT,
  // Attachments read as gun parts, so they borrow the weapon's gunmetal.
  attachment: ITEM_COLOR_WEAPON,
  // Dark olive steel: a grenade on the ground is a small dark thing.
  grenade: ITEM_COLOR_ARMOR,
};

/** One item on the ground, as tracked. Handed to `game/interact.ts` as-is. */
export interface DroppedItem {
  id: string;
  defId: string;
  pos: Vec3;
  /** Display name from the item DB, or the raw def id for an unknown item. */
  name: string;
}

export class DroppedItems {
  private readonly scene: THREE.Scene;
  private readonly geo: THREE.BoxGeometry;
  private readonly mat: THREE.MeshLambertMaterial;
  private readonly mesh: THREE.InstancedMesh;

  /** Insertion-ordered, which is also the draw order — `Map` guarantees it. */
  private readonly tracked = new Map<string, DroppedItem>();
  private readonly list: DroppedItem[] = [];

  /** Scratch: rebuilding must not allocate per item. */
  private readonly sPos = new THREE.Vector3();
  private readonly sQuat = new THREE.Quaternion();
  private readonly sEuler = new THREE.Euler();
  private readonly sScale = new THREE.Vector3(1, 1, 1);
  private readonly sMat = new THREE.Matrix4();
  private readonly sColor = new THREE.Color();

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    this.geo = new THREE.BoxGeometry(ITEM_SIZE_M, ITEM_SIZE_M, ITEM_SIZE_M);
    // Lambert and flat-shaded, exactly like every other box in the game: a
    // dropped item must sit in the same light as the crate it fell out of.
    this.mat = new THREE.MeshLambertMaterial({ flatShading: true });
    this.mesh = new THREE.InstancedMesh(this.geo, this.mat, MAX_DRAWN);
    this.mesh.name = "worldItems";
    this.mesh.count = 0;
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    // Items are scattered over the whole map, so one bounding sphere around
    // the pool would be permanently wrong.
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
  }

  /** Everything currently on the ground, for the interact raycast. */
  get items(): readonly DroppedItem[] {
    return this.list;
  }

  /** The full set, as `worldItems` sends it after `welcome`. Replaces. */
  setAll(items: readonly WorldItemSnap[]): void {
    this.tracked.clear();
    for (const it of items) this.tracked.set(it.id, toDropped(it));
    this.rebuild();
  }

  /** One item hit the ground (`itemDropped`). */
  add(item: WorldItemSnap): void {
    this.tracked.set(item.id, toDropped(item));
    this.rebuild();
  }

  /** ...and it is gone again (`itemTaken`). */
  remove(id: string): void {
    if (!this.tracked.delete(id)) return;
    this.rebuild();
  }

  /** Map change or disconnect: the ground is somebody else's now. */
  clear(): void {
    if (this.tracked.size === 0 && this.list.length === 0) return;
    this.tracked.clear();
    this.rebuild();
  }

  dispose(): void {
    this.scene.remove(this.mesh);
    this.mesh.dispose();
    this.geo.dispose();
    this.mat.dispose();
    this.tracked.clear();
    this.list.length = 0;
  }

  // -----------------------------------------------------------------------

  /** Rewrites the instance buffer from the tracked set. Change-driven only. */
  private rebuild(): void {
    this.list.length = 0;
    for (const item of this.tracked.values()) this.list.push(item);

    const drawn = Math.min(this.list.length, MAX_DRAWN);
    for (let i = 0; i < drawn; i++) {
      const item = this.list[i] as DroppedItem;

      // Yaw from a hash of the id: not perfectly parallel, and the SAME angle
      // on
      // every client and reconnect.
      this.sEuler.set(0, rand01(hashString(item.id)) * Math.PI * 2, 0);
      this.sQuat.setFromEuler(this.sEuler);
      // `pos` is where the item came to rest, i.e. its underside.
      this.sPos.set(item.pos.x, item.pos.y + ITEM_SIZE_M * 0.5, item.pos.z);
      this.sMat.compose(this.sPos, this.sQuat, this.sScale);
      this.mesh.setMatrixAt(i, this.sMat);

      this.sColor.setHex(colorFor(item.defId));
      this.mesh.setColorAt(i, this.sColor);
    }

    this.mesh.count = drawn;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor !== null) this.mesh.instanceColor.needsUpdate = true;
  }
}

function toDropped(it: WorldItemSnap): DroppedItem {
  const def = getItemDef(it.defId);
  return {
    id: it.id,
    defId: it.defId,
    pos: { x: it.pos.x, y: it.pos.y, z: it.pos.z },
    // An item this client's DB has never heard of (a server one content
    // version ahead) still gets a box and a prompt — it just says the raw id.
    name: def === null ? it.defId : def.name,
  };
}

function colorFor(defId: string): number {
  const def = getItemDef(defId);
  return def === null ? ITEM_COLOR_LOOT : KIND_COLORS[def.kind];
}
